import React from 'react'
import {
    Button,
    Chip,
    DialogActions,
    Grid,
    TextField,
    Typography,
    makeStyles
} from '@material-ui/core'
import { BusinessOutlined as TenantIcon } from '@material-ui/icons'

import { useAddUserMutation } from 'components/api'
import { useValidation } from 'components'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import AdminModal from 'components/admin/AdminModal'

const useStyles = makeStyles({
    root: {
        padding: 20
    },
    textfield: {
        marginTop: 6
    },
    tenantBlock: {
        marginBottom: 12
    },
    tenantChip: {
        marginTop: 6,
        fontWeight: 600
    },
    hint: {
        marginTop: 12,
        color: '#5e5e5e'
    },
    buttonContainer: {
        paddingTop: 20,
        borderTop: '1px solid #dee2e6'
    },
    closeButton: {
        color: '#ef8c25'
    }
})

const defaultValues = { given_name: '', family_name: '', email: '' }

function InviteUserDialog({ tenantLabel, onInvited, onError }, ref) {
    const classes = useStyles()
    const { mutateAsync: addUserAsync, isLoading } = useAddUserMutation()

    const [open, setOpen] = React.useState(false)
    const [values, setValues] = React.useState(defaultValues)
    const [duplicate, setDuplicate] = React.useState(false)

    const { validate, errors, ifError, reset: resetValidation } = useValidation({
        given_name: 'required',
        family_name: 'required',
        email: ['required', 'email']
    })

    const onChange = React.useCallback(e => {
        const { name, value } = e.target
        setValues(prev => ({ ...prev, [name]: value }))
        setDuplicate(false)
        resetValidation()
    }, [resetValidation])

    const close = React.useCallback(() => {
        setValues(defaultValues)
        setDuplicate(false)
        setOpen(false)
    }, [])

    React.useImperativeHandle(ref, () => ({
        open: () => {
            setValues(defaultValues)
            setDuplicate(false)
            setOpen(true)
        }
    }))

    const onSubmit = React.useCallback(async e => {
        e.preventDefault()
        if (!validate(values)) return
        try {
            await addUserAsync(values)
            if (onInvited) onInvited(values.email)
            close()
        } catch (error) {
            if (error.response && error.response.status === 409) {
                setDuplicate(true)
                return
            }
            if (onError) onError(getApiErrorMessage(error, 'Failed to invite user'))
        }
    }, [values, validate, addUserAsync, onInvited, onError, close])

    return (
        <AdminModal open={open} onClose={close} title="Invite User">
            <Grid container className={classes.root} spacing={2}>
                <Grid item xs={12} className={classes.tenantBlock}>
                    <Typography variant="caption" color="textSecondary">Tenant</Typography>
                    <div>
                        <Chip
                            icon={<TenantIcon />}
                            label={tenantLabel || 'Current tenant'}
                            color="primary"
                            size="small"
                            className={classes.tenantChip}
                        />
                    </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                    First Name
                    <TextField
                        placeholder="First name"
                        name="given_name"
                        value={values.given_name}
                        onChange={onChange}
                        error={errors.given_name}
                        helperText={ifError('given_name', 'Please fill in the first name')}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        size="small"
                        className={classes.textfield}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    Last Name
                    <TextField
                        placeholder="Last name"
                        name="family_name"
                        value={values.family_name}
                        onChange={onChange}
                        error={errors.family_name}
                        helperText={ifError('family_name', 'Please fill in the last name')}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        size="small"
                        className={classes.textfield}
                    />
                </Grid>
                <Grid item xs={12}>
                    Email
                    <TextField
                        placeholder="name@example.com"
                        name="email"
                        value={values.email}
                        onChange={onChange}
                        error={errors.email || duplicate}
                        helperText={
                            ifError('email', 'Please fill in a valid email') ||
                            (duplicate && 'A user with that email already exists.')
                        }
                        variant="outlined"
                        color="primary"
                        fullWidth
                        size="small"
                        className={classes.textfield}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" className={classes.hint}>
                        The user is added to <strong>{tenantLabel || 'this tenant'}</strong> and Cognito
                        sends a temporary-password invite email. Roles can be granted afterward from the
                        user's Manage panel.
                    </Typography>
                </Grid>
            </Grid>
            <DialogActions>
                <Grid container className={classes.buttonContainer} justify="flex-end" spacing={2}>
                    <Grid item>
                        <Button onClick={close} className={classes.closeButton}>Cancel</Button>
                    </Grid>
                    <Grid item>
                        <Button
                            onClick={onSubmit}
                            variant="contained"
                            color="primary"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Inviting…' : 'Invite User'}
                        </Button>
                    </Grid>
                </Grid>
            </DialogActions>
        </AdminModal>
    )
}

export default React.forwardRef(InviteUserDialog)
